// client/src/components/team/TeamManagement.js
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Mail,
  X,
  RefreshCw,
  UserPlus,
  Trash2,
  Shield,
  User
} from 'lucide-react';

const TeamManagement = () => {
  const { user } = useAuth();
  const { t } = useTranslation();

  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState('member');
  const [canManageTeam, setCanManageTeam] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    email: '',
    firstName: '',
    lastName: '',
    preferredLanguage: 'it'
  });
  const [inviteLoading, setInviteLoading] = useState(false);

  // ✅ Use useCallback for fetchTeamData to avoid useEffect warning
  const fetchTeamData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/team/members');
      setTeamMembers(response.data.teamMembers);
      setPendingInvites(response.data.pendingInvites);
      setOrganization(response.data.organization);
      setCurrentUserRole(response.data.currentUserRole || 'member');
      setCanManageTeam(response.data.canManageTeam || false);
    } catch (err) {
      setError(err.response?.data?.message || t('team.errors.fetchFailed'));
      console.error('Error fetching team data:', err);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  // ✅ Enhanced error handling section - similar to BankManagement
  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.post('/api/team/invite', inviteForm);
      setSuccess(t('team.inviteSent'));
      setInviteForm({ email: '', firstName: '', lastName: '',preferredLanguage: 'it' });
      setShowInviteForm(false);
      fetchTeamData();
    } catch (err) {
      const errorCode = err.response?.data?.errorCode;
      const errorMessage = err.response?.data?.message;
      
      // ✅ CLOSE THE POPUP BEFORE SHOWING THE ERROR
      setShowInviteForm(false);
      
      let displayError;
      
      // Handle specific error codes
      switch (errorCode) {
        case 'userAlreadyExists':
          displayError = t('team.errors.userAlreadyExists') || 'User already exists in the system';
          break;
        case 'userInOrganization':
          displayError = t('team.errors.userInOrganization') || 'This email is already registered in your organization';
          break;
        case 'invitationPending':
          displayError = t('team.errors.invitationPending') || 'An invitation for this email is already pending';
          break;
        case 'validEmailRequired':
          displayError = t('team.errors.validEmailRequired') || 'Valid email is required';
          break;
        case 'emailSendFailed':
          displayError = t('team.errors.emailSendFailed') || 'Failed to send invitation email. Please try again.';
          break;
        default:
          // Fallback to original message or generic error
          displayError = errorMessage || t('team.errors.inviteFailed') || 'Failed to send invitation';
      }
      
      setError(displayError);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await axios.post(`/api/team/invite/${inviteId}/resend`);
      setSuccess(t('team.inviteResent'));
      fetchTeamData();
    } catch (err) {
      const errorCode = err.response?.data?.errorCode;
      const errorMessage = errorCode 
        ? t(`team.errors.${errorCode}`)
        : err.response?.data?.message || t('team.errors.resendFailed');
      setError(errorMessage);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (window.confirm(t('team.confirmCancelInvite'))) {
      try {
        await axios.delete(`/api/team/invite/${inviteId}`);
        setSuccess(t('team.inviteCancelled'));
        fetchTeamData();
      } catch (err) {
        const errorCode = err.response?.data?.errorCode;
        const errorMessage = errorCode 
          ? t(`team.errors.${errorCode}`)
          : err.response?.data?.message || t('team.errors.cancelFailed');
        setError(errorMessage);
      }
    }
  };

  const handleRemoveMember = async (userId, userName, userRole) => {
    // ✅ PREVENT REMOVING OWNERS
    if (userRole === 'owner') {
      setError('Cannot remove organization owner');
      return;
    }

    if (window.confirm(t('team.confirmRemoveMember', { name: userName }))) {
      try {
        await axios.delete(`/api/team/member/${userId}`);
        setSuccess(t('team.memberRemoved'));
        fetchTeamData();
      } catch (err) {
        const errorCode = err.response?.data?.errorCode;
        const errorMessage = errorCode 
          ? t(`team.errors.${errorCode}`)
          : err.response?.data?.message || t('team.errors.removeFailed');
        setError(errorMessage);
      }
    }
  };

  const resetInviteForm = () => {
    setInviteForm({ email: '', firstName: '', lastName: '',preferredLanguage: 'it' });
    setShowInviteForm(false);
    setError('');
  };

  if (loading) {
    return (
      <div className="team-management">
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="company-dashboard">
      <div className="card">
        {/* Card Header */}
        <div className="card-title">
          <Users className="card-title-icon" size={20} />
          <span>{t('team.title')}</span>
        </div>

        {/* Action row */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <span className={`role-badge ${currentUserRole}`}>
            {currentUserRole === 'owner' && <Shield size={12} />}
            {currentUserRole === 'member' && <User size={12} />}
            {currentUserRole === 'owner' ? t('team.organizationOwner') : t('team.teamMember')}
          </span>
          {canManageTeam && (
            <button className="btn btn-primary" onClick={() => setShowInviteForm(true)}>
              <UserPlus size={18} />
              {t('team.inviteMember')}
            </button>
          )}
        </div>

        {/* ✅ PERMISSION NOTICE FOR MEMBERS */}
        {!canManageTeam && (
          <div className="alert alert-info">
            <strong>{t('team.infoLabel')}:</strong> {t('team.ownerOnlyMessage')}
          </div>
        )}

        {/* Alerts */}
        {error && (
          <div className="alert alert-danger alert-dismissible">
            {error}
            <button className="close" onClick={() => setError('')}>&times;</button>
          </div>
        )}
        {success && (
          <div className="alert alert-success alert-dismissible">
            {success}
            <button className="close" onClick={() => setSuccess('')}>&times;</button>
          </div>
        )}

        {/* Organization Info */}
        {organization && (
          <div className="organization-info" style={{ marginBottom: '16px', padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: 0, fontSize: '16px' }}>{organization.name}</h3>
            <p className="text-muted" style={{ margin: '4px 0 0' }}>
              {t(`team.organizationType.${organization.type}`)} •{' '}
              {t('team.totalMembers', { count: teamMembers.length })}
            </p>
          </div>
        )}

      {/* Active Team Members */}
      <div className="team-section">
        <h2>
          <Users size={20} />
          {t('team.activeMembers')}
          <span className="count-badge">{teamMembers.length}</span>
        </h2>

        {teamMembers.length === 0 ? (
          <div className="empty-state">
            <Users size={48} className="empty-icon" />
            <h3>{t('team.noMembers')}</h3>
            {canManageTeam && (
              <button className="btn btn-primary" style={{ marginTop: '16px' }} onClick={() => setShowInviteForm(true)}>
                <UserPlus size={18} />
                {t('team.inviteFirstMember') || 'Invita il primo membro'}
              </button>
            )}
          </div>
        ) : (
          <div className="team-grid">
            {teamMembers.map((member) => (
              <div key={member._id} className="member-card">
                <div className="member-info">
                  <div className="member-avatar">
                    {member.firstName?.[0]}{member.lastName?.[0]}
                  </div>
                  <div className="member-details">
                    <div className="member-name-role">
                      <h4>{member.firstName} {member.lastName}</h4>
                      {/* ✅ SHOW ORGANIZATION ROLE BADGE */}
                      <span className={`role-badge ${member.organizationRole || 'member'}`}>
                        {member.organizationRole === 'owner' ? (
                          <>
                            <Shield size={12} />
                            {t('team.owner')}
                          </>
                        ) : (
                          <>
                            <User size={12} />
                            {t('team.member')}
                          </>
                        )}
                      </span>
                    </div>
                    <p className="member-email">{member.email}</p>
                    <p className="member-position">
                      {member.position || t('team.noPosition')}
                    </p>
                  </div>
                </div>

                <div className="member-meta" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {/* ✅ ONLY SHOW REMOVE BUTTON FOR OWNERS AND NOT FOR CURRENT USER OR OTHER OWNERS */}
                  {canManageTeam && 
                   member._id !== user.id && 
                   member.organizationRole !== 'owner' && (
                    <button
                      className="btn-icon btn-danger"
                      onClick={() => handleRemoveMember(
                        member._id, 
                        `${member.firstName} ${member.lastName}`,
                        member.organizationRole
                      )}
                      title={t('team.removeMember')}
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ✅ PENDING INVITES - ONLY SHOW TO OWNERS */}
      {pendingInvites.length > 0 && canManageTeam && (
        <div className="team-section">
          <h2>
            <Mail size={20} />
            {t('team.pendingInvites')}
            <span className="count-badge">{pendingInvites.length}</span>
          </h2>

          <div className="invites-list">
            {pendingInvites.map((invite) => (
              <div key={invite._id} className="invite-card">
                <div className="invite-info">
                  <div className="invite-avatar">
                    {invite.firstName?.[0]}{invite.lastName?.[0]}
                  </div>
                  <div className="invite-details">
                    <div className="invite-email">{invite.email}</div>
                    <div className="invite-meta">
                      <strong>{invite.firstName} {invite.lastName}</strong>
                      <br/>
                      <small>
                        {t('team.invitedBy')}: {invite.invitedBy.firstName} {invite.invitedBy.lastName} •{' '}
                        {t('team.expires')}: {new Date(invite.expiresAt).toLocaleDateString()}
                      </small>
                    </div>
                  </div>
                </div>

                <div className="invite-actions">
                  <button
                    className="btn-icon btn-primary"
                    onClick={() => handleResendInvite(invite._id)}
                    title={t('team.resendInvite')}
                  >
                    <RefreshCw size={16} />
                  </button>
                  <button
                    className="btn-icon btn-danger"
                    onClick={() => handleCancelInvite(invite._id)}
                    title={t('team.cancelInvite')}
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      </div>

    {/* ✅ INVITE FORM MODAL - ONLY FOR OWNERS */}
    {showInviteForm && canManageTeam && createPortal(
      <div className="modal-overlay" onClick={resetInviteForm}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h3>{t('team.inviteNewMember')}</h3>
            <button className="close-btn" onClick={resetInviteForm}>
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleInviteSubmit}>
            <div className="form-group">
              <label>{t('team.email')} *</label>
              <input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                required
                placeholder={t('team.emailPlaceholder')}
                disabled={inviteLoading}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>{t('team.firstName')}</label>
                <input
                  type="text"
                  value={inviteForm.firstName}
                  onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                  placeholder={t('team.optional')}
                  disabled={inviteLoading}
                />
              </div>
              <div className="form-group">
                <label>{t('team.lastName')}</label>
                <input
                  type="text"
                  value={inviteForm.lastName}
                  onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                  placeholder={t('team.optional')}
                  disabled={inviteLoading}
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('team.preferredLanguage')}</label>
              <select
                value={inviteForm.preferredLanguage}
                onChange={(e) => setInviteForm({ ...inviteForm, preferredLanguage: e.target.value })}
                disabled={inviteLoading}
                className="form-control"
              >
                <option value="it">Italiano</option>
                <option value="en">English</option>
              </select>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={resetInviteForm} disabled={inviteLoading}>
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={inviteLoading}>
                {inviteLoading ? (
                  <>
                    <div className="spinner-sm"></div>
                    {t('common.sending')}
                  </>
                ) : (
                  t('team.sendInvite')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    , document.body)}
  </div>
);
};

export default TeamManagement;